import React from "react";
import { Card, Col, Row, Alert } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faWallet,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ButtonComponent from "../../../../components/ButtonComponent";
import {
  useTopUpWalletMutation,
  useGetCustomerByIdQuery,
  useGetWalletBalanceQuery,
} from "../../../../redux/services/customerService";
import "./index.css";

interface TopUpWalletFormData {
  amount: number;
  notes?: string;
}

interface CustomerTopUpWalletProps {
  customerId?: string;
}

const CustomerTopUpWallet: React.FC<CustomerTopUpWalletProps> = ({
  customerId,
}) => {
  const { id } = useParams<{ id: string }>();
  const custId = customerId || id;

  // Fetch customer data using RTK Query
  const {
    data: customer,
    isLoading: isCustomerLoading,
    error: customerError,
  } = useGetCustomerByIdQuery(custId!, {
    skip: !custId,
  });

  const {
    data: walletbalance,
    isLoading: isLoadingWalletBalance,
    error: errorLoadingWalletBalance,
    refetch: refetchWalletBalance,
  } = useGetWalletBalanceQuery(custId!, {
    skip: !custId,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<TopUpWalletFormData>({
    defaultValues: {
      amount: 0,
      notes: "",
    },
  });

  const [topUpWallet, { isLoading: isTopUpLoading }] = useTopUpWalletMutation();
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const amount = watch("amount");

  const onSubmit = async (data: TopUpWalletFormData) => {
    if (!customer?.email) {
      toast.error("Customer email is required for top-up");
      return;
    }

    if (!custId) {
      toast.error("Customer ID is required");
      return;
    }

    try {
      await topUpWallet({
        email: customer.email,
        amount: +data.amount,
      }).unwrap();

      // Refetch wallet balance after successful top-up
      refetchWalletBalance();

      reset({
        amount: 0,
        notes: "",
      });
      toast.success("Wallet topped up successfully!");
    } catch (error: any) {
      const errorMessage =
        error?.data?.message || "Failed to top up wallet. Please try again.";
      toast.error(errorMessage);
    }
  };

  const isLoading = isCustomerLoading || isTopUpLoading;

  if (customerError) {
    return (
      <div
        className="d-flex justify-content-center align-items-center flex-column"
        style={{ minHeight: "200px" }}
      >
        <Alert variant="danger">
          Error loading customer data. Please try again.
        </Alert>
      </div>
    );
  }

  return (
    <div className="customer-topup-wallet">
      {/* Customer Info Card */}
      {customer && (
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={8}>
                <h6 className="mb-1 text-primary">{customer.displayName}</h6>
                <p className="text-muted mb-1">
                  <strong>Email:</strong> {customer.email || "Not provided"}
                </p>
                <p className="text-muted mb-0">
                  <strong>Phone:</strong>{" "}
                  {customer.phoneNumber || "Not provided"}
                </p>
              </Col>
              <Col md={4} className="text-md-start">
                <div className="wallet-balance-section">
                  <div className="d-flex align-items-center justify-content-md-start mb-2">
                    <FontAwesomeIcon icon={faWallet} className="text-success me-2" />
                    <span className="fw-bold text-muted">Current Balance</span>
                    <button
                      type="button"
                      onClick={() => refetchWalletBalance()}
                      className="btn btn-sm btn-link p-1 ms-2"
                      title="Refresh Balance"
                      disabled={isLoadingWalletBalance}
                    >
                      <FontAwesomeIcon 
                        icon={faRefresh} 
                        spin={isLoadingWalletBalance}
                        className="text-primary"
                      />
                    </button>
                  </div>
                  {isLoadingWalletBalance ? (
                    <div className="text-start">
                      <FontAwesomeIcon icon={faSpinner} spin className="text-primary me-2" />
                      Loading...
                    </div>
                  ) : errorLoadingWalletBalance ? (
                    <div className="text-start">
                      <div className="text-danger small">
                        Error loading balance
                      </div>
                    </div>
                  ) : (
                    <div className="text-start">
                      <h4 className="text-success mb-0">
                        {walletbalance?.walletBalance?.toFixed(2) || "0.00"}
                      </h4>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Loading State */}
      {isCustomerLoading && (
        <Card className="mb-4 shadow-sm">
          <Card.Body className="text-center">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <div className="mt-2">Loading customer information...</div>
          </Card.Body>
        </Card>
      )}

      {/* Top Up Form Card */}
      <Card className="shadow-sm">
        <Card.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col lg={6}>
                <ControlledRowTextField
                  label="Amount"
                  placeholder="Enter amount to top up"
                  inputName={"amount"}
                  inputType={"number"}
                  control={control}
                  rules={{
                    required: "Amount is required",
                    min: {
                      value: 1,
                      message: "Amount must be greater than 0",
                    },
                    max: {
                      value: 100000,
                      message: "Amount cannot exceed $100,000",
                    },
                  }}
                  errors={errors}
                />
                {errors.amount && (
                  <div className="text-danger mt-1 small">
                    {errors.amount.message}
                  </div>
                )}
              </Col>
            </Row>


            <div className="d-flex justify-content-end mt-4">
              <ButtonComponent
                titel={isTopUpLoading ? "Processing..." : "Top Up Wallet"}
                type="submit"
                isDisabled={isLoading || !customer?.email}
                icon={
                  isTopUpLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : null
                }
              />
            </div>
          </form>
        </Card.Body>
      </Card>

    </div>
  );
};

export default CustomerTopUpWallet;
